import { logger, type ThemeTypes, usePreferredColorScheme } from '@talos/kyoko';
import { useMemo } from 'react';
import { useTheme } from 'styled-components';
import { useFeatureFlag } from '../../hooks/useFeatureFlag';

type AssetResult = { state: 'empty' } | { state: 'client'; filepath: string };
type LogoResult = AssetResult | { state: 'talos' };

interface UseClientLogoOptions {
  collapsed: boolean;
  takeBrowserTheme?: boolean;
}

export function useClientLogo({ collapsed, takeBrowserTheme = false }: UseClientLogoOptions): LogoResult {
  const { type } = useTheme();
  const preferredColorScheme = usePreferredColorScheme();
  const { clientLogoName } = useFeatureFlag();

  return useMemo(() => {
    // No client logo is set
    if (clientLogoName === '') {
      return { state: 'talos' };
    }

    // Should never be undefined since default is "" but just in case
    if (!clientLogoName) {
      return { state: 'empty' };
    }

    const theme = takeBrowserTheme ? preferredColorScheme : type;

    return deriveAssetFromBaseName(clientLogoName, collapsed, theme);
  }, [clientLogoName, collapsed, takeBrowserTheme, preferredColorScheme, type]);
}

function deriveAssetFromBaseName(baseName: string, collapsed: boolean, theme: ThemeTypes): AssetResult {
  const trimmedBaseName = baseName.trim();

  if (trimmedBaseName === '') {
    return { state: 'empty' };
  }

  // Extract the file extension from the base name
  const lastDotIndex = trimmedBaseName.lastIndexOf('.');
  if (lastDotIndex === -1) {
    logger.warn(`Asset base name must include file extension: ${JSON.stringify(trimmedBaseName)}`);
    return { state: 'empty' };
  }

  const name = trimmedBaseName.substring(0, lastDotIndex);
  const extension = trimmedBaseName.substring(lastDotIndex);

  const size = collapsed ? 'collapsed' : 'expanded';
  const fileName = `${name}-${theme}-${size}${extension}`;

  const fullPath = `/assets/logos/${fileName}`;
  const validatedPath = validateAssetPath(fullPath);

  if (!validatedPath) {
    logger.warn(`Asset path does not match safe pattern: ${JSON.stringify(fullPath)}`);
    return { state: 'empty' };
  }

  return { state: 'client', filepath: validatedPath };
}

// Prevents loading arbitrary URLs and ensures only files from `/assets/logos/` can be loaded.
function validateAssetPath(assetPath: string): string | null {
  const SAFE_ASSET_PATH_PATTERN = /^\/assets\/logos\/[a-zA-Z0-9._-]+\.(svg|png|jpg|jpeg|webp|ico)$/;

  const trimmed = assetPath.trim();
  if (SAFE_ASSET_PATH_PATTERN.test(trimmed)) {
    return trimmed;
  }

  return null;
}
